'use client';

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import {
	ENUM_UTILS,
	fetchAuctionOrderParams,
	MarketId,
	UIMarket,
} from '@velocity-exchange/common';
import {
	PositionDirection,
	BigNum,
	BASE_PRECISION_EXP,
	BN,
	OrderType,
	PRICE_PRECISION_EXP,
	MarketType,
	ZERO,
} from '@velocity-exchange/sdk';
import useOraclePrice from 'src/hooks/useOraclePrice';
import useMarkPrice from 'src/hooks/useMarkPrice';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import { useUserProtectionSlotClock } from 'src/hooks/useSlotClock';
import { useDebounce } from 'react-use';

import Env, {
	syncGetCurrentSettings,
} from 'src/environmentVariables/EnvironmentVariables';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import { calculateShowPriceEstimateOracleDivergenceWarning } from 'src/utils/priceImpactUtils';
import { SlippageTolerance } from 'src/@types/types';
import { ORDER_PREP_UTILS } from 'src/stores/VelocityStore/actions/actionHelpers/orderPrep';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

const FETCH_INTERVAL_MS = 3000;

export type PreviewOrderParams = {
	auctionStartPrice: BN;
	auctionEndPrice: BN;
	auctionDuration: number;
	slippage: number; // This will now come from the server response when available
	constrainedBySlippage?: boolean;
	isOracleOrder?: boolean;
	isDivergentFromOracle?: boolean;
	priceImpact?: {
		markPrice: BN;
		entryPrice: BN;
		priceImpact: BN;
		baseAvailable: BN;
		bestPrice: BN;
		worstPrice: BN;
		priceImpactInputBaseSize: BN;
		showPriceEstimateOracleDivergenceWarning: boolean;
		exceedsLiquidity: boolean;
		marketIndex: number;
		marketType: MarketType;
	};
};

// Helper function to create a stable cache key
const createQueryKey = (params: {
	marketKey: string;
	baseSize: string;
	side: string;
	orderType: string;
	slippageTolerance: number | string;
	maxLeverageSelected: boolean;
	reduceOnly: boolean;
}) => [
	'previewOrder',
	params.marketKey,
	params.baseSize,
	params.side,
	params.orderType,
	params.slippageTolerance,
	params.maxLeverageSelected,
	params.reduceOnly,
];

// Helper function to round base size to reduce cache fragmentation
// Only re-fetch if base size changes by more than 1%
const roundBaseSizeForCaching = (baseSize: BN): string => {
	if (baseSize.isZero()) return '0';

	// Handle very large numbers (like MAX_LEVERAGE_ORDER_SIZE)
	// by using a simpler approach to prevent Number conversion errors
	if (baseSize.gt(new BN(Number.MAX_SAFE_INTEGER))) {
		// For very large numbers, just use the string representation
		// since we're mainly trying to prevent cache fragmentation
		return baseSize.toString();
	}

	const baseSizeNum = baseSize.toNumber();
	// Round to 2 significant digits to prevent excessive cache fragmentation
	// while still being sensitive to meaningful changes
	const magnitude = Math.floor(Math.log10(Math.abs(baseSizeNum)));
	const divisor = Math.pow(10, magnitude - 1);
	const rounded = Math.round(baseSizeNum / divisor) * divisor;

	return rounded.toString();
};

/* Hook to fetch cached preview order params from the current selected market or optionally, a passed in marketId */
/* If oracle order, it will automatically return the offsets to display more digestible values to the user  */
const usePreviewOrder = (
	marketId?: MarketId,
	overrides?: {
		baseSizeStringValue?: string;
		side?: 'buy' | 'sell';
		orderType?: string;
		slippageTolerance?: number;
		maxLeverageSelected?: boolean;
		reduceOnly?: boolean;
	}
): PreviewOrderParams => {
	const tradeForm = useVelocityStore((s) => s.tradeForm);
	const selectedMarketId = useVelocityStore(
		(s) => s.selectedMarket.current?.marketId
	);
	const targetMarketId = marketId ?? selectedMarketId;

	const getMarketInfo = useMarketsInfoStore(
		(s) => s.getMarketInfoByIndexAndType
	);

	const oraclePrice = useOraclePrice(targetMarketId as MarketId);
	const markPrice = useMarkPrice(targetMarketId);

	// For fallback functionality
	const velocityClient = useVelocityClient();
	const currentUserAccount = useCurrentUserAccount();

	// Preview must convert the trader's ramp with the same clock execution uses,
	// or the previewed auction and the submitted one disagree.
	const { slotDurationMs } = useUserProtectionSlotClock();

	const {
		slippageTolerance: tradeFormSlippageTolerance,
		baseSizeStringValue: tradeFormBaseSizeStringValue,
		maxLeverageSelected: tradeFormMaxLeverageSelected,
		orderType: tradeFormOrderType,
		side: tradeFormSide,
		reduceOnly: tradeFormReduceOnly,
	} = tradeForm;

	// Use overrides if provided, otherwise fall back to trade form
	const slippageTolerance =
		overrides?.slippageTolerance ?? tradeFormSlippageTolerance;
	const baseSizeStringValue =
		overrides?.baseSizeStringValue ?? tradeFormBaseSizeStringValue;
	const maxLeverageSelected =
		overrides?.maxLeverageSelected ?? tradeFormMaxLeverageSelected;
	const orderType = overrides?.orderType ?? tradeFormOrderType;
	const side = overrides?.side ?? tradeFormSide;
	const reduceOnly = overrides?.reduceOnly ?? tradeFormReduceOnly;

	// Create stable query parameters
	const queryParams = useMemo(() => {
		const currentBaseSize = parseInputBigNumOrZero(
			baseSizeStringValue,
			BASE_PRECISION_EXP
		).val;

		return {
			marketKey: targetMarketId?.key,
			baseSize: roundBaseSizeForCaching(currentBaseSize),
			side,
			orderType,
			slippageTolerance,
			maxLeverageSelected,
			reduceOnly,
			// Include these for the query function
			actualBaseSize: currentBaseSize,
			marketId: targetMarketId,
		};
	}, [
		targetMarketId?.key,
		baseSizeStringValue,
		maxLeverageSelected,
		side,
		orderType,
		slippageTolerance,
		reduceOnly,
		selectedMarketId,
		// Add override dependencies
		overrides?.baseSizeStringValue,
		overrides?.side,
		overrides?.orderType,
		overrides?.slippageTolerance,
		overrides?.maxLeverageSelected,
		overrides?.reduceOnly,
	]);

	// Create debounced query parameters to prevent excessive requests during slider dragging
	const [debouncedQueryParams, setDebouncedQueryParams] = useState(queryParams);

	// Use useDebounce to delay updating the debounced params until user stops changing values
	useDebounce(
		() => {
			setDebouncedQueryParams(queryParams);
		},
		300, // 300ms delay
		[queryParams]
	);

	// Use TanStack Query for caching and data fetching (only if QueryClient is available)
	const { data: previewOrderParams } = useQuery({
		queryKey: createQueryKey(debouncedQueryParams as any),
		queryFn: async (): Promise<PreviewOrderParams | null> => {
			const currentSettings = syncGetCurrentSettings();

			// return if prices/settings not loaded yet
			if (!currentSettings || !debouncedQueryParams.marketId) return null;
			if (!velocityClient) return null;
			if (!currentUserAccount?.client) return null;
			if (
				(!oraclePrice || oraclePrice.eqZero()) &&
				(!markPrice || markPrice.eqZero())
			)
				return null;

			const marketAccount = getMarketInfo(
				debouncedQueryParams.marketId.marketIndex,
				debouncedQueryParams.marketId.marketType
			)?.account;

			if (!marketAccount) return null;

			// Use placeholder values initially, will be replaced by server response
			const placeholderPriceImpact = {
				markPrice: markPrice?.val || ZERO,
				entryPrice: oraclePrice?.val || ZERO,
				priceImpact: ZERO,
				baseAvailable: ZERO,
				bestPrice: oraclePrice?.val || ZERO,
				worstPrice: oraclePrice?.val || ZERO,
				priceImpactInputBaseSize: debouncedQueryParams.actualBaseSize,
				showPriceEstimateOracleDivergenceWarning: false,
				exceedsLiquidity: false,
				marketIndex: debouncedQueryParams.marketId.marketIndex,
				marketType: debouncedQueryParams.marketId.marketType,
			};

			// even if we are previewing a limit order, we get the auctionStartPrice for a market order
			// so we know to add auction params to the limit order if the limit price is worse
			const previewOrderType: 'market' | 'oracle' =
				orderType === 'market' || orderType === 'oracle' ? orderType : 'market';

			const optionalAuctionParamsInputs =
				ORDER_PREP_UTILS.getOptionalAuctionParamsInputs({
					maxLeverageSelected: !!maxLeverageSelected,
					uiMarket: UIMarket.fromMarketId(debouncedQueryParams.marketId),
					slippageTolerance: slippageTolerance as SlippageTolerance,
					marketAccount,
					slotDuration: slotDurationMs,
				});

			const forceFallback = useFeatureFlagStore
				.getState()
				.flagEnabled('FORCE_ORDER_PARAMS_FALLBACK');

			const { orderParams: auctionParams, meta } =
				await fetchAuctionOrderParams({
					velocityClient,
					user: currentUserAccount.client,
					assetType: 'base',
					marketType: debouncedQueryParams.marketId.marketType,
					marketIndex: debouncedQueryParams.marketId.marketIndex,
					direction:
						side === 'buy' ? PositionDirection.LONG : PositionDirection.SHORT,
					amount: debouncedQueryParams.actualBaseSize,
					reduceOnly,
					dlobServerHttpUrl: Env.dlobServerHttpUrl,
					slotDuration: slotDurationMs,
					optionalAuctionParamsInputs,
					forceFallback,
				});

			const finalSlippageToUse =
				meta.slippage ??
				(typeof slippageTolerance === 'number' ? slippageTolerance : 0.1);

			// Extract price impact data - already processed as BN values
			let priceImpactData: PreviewOrderParams['priceImpact'] =
				placeholderPriceImpact;
			if (meta.priceImpact) {
				const pi = meta.priceImpact;
				priceImpactData = {
					entryPrice: pi.entryPrice,
					markPrice: pi.markPrice,
					priceImpact: pi.priceImpact,
					baseAvailable: pi.baseAvailable ?? ZERO,
					bestPrice: pi.bestPrice,
					worstPrice: pi.worstPrice,
					priceImpactInputBaseSize: debouncedQueryParams.actualBaseSize,
					showPriceEstimateOracleDivergenceWarning:
						calculateShowPriceEstimateOracleDivergenceWarning(
							pi.entryPrice,
							pi.oraclePrice,
							side,
							previewOrderType,
							!debouncedQueryParams.actualBaseSize.isZero()
						),
					exceedsLiquidity: pi.exceedsLiquidity ?? false,
					marketIndex: debouncedQueryParams.marketId.marketIndex,
					marketType: debouncedQueryParams.marketId.marketType,
				};
			}

			// if it was a limit order and not crossing, or auctions are disabled, return nothing
			if (!auctionParams?.auctionDuration) return null;

			const isOracleOrder = ENUM_UTILS.match(
				auctionParams.orderType,
				OrderType.ORACLE
			);

			const auctionStartPrice = isOracleOrder
				? oraclePrice?.val.add(auctionParams?.auctionStartPrice ?? ZERO)
				: auctionParams.auctionStartPrice;

			const auctionEndPrice = isOracleOrder
				? oraclePrice?.val.add(auctionParams?.auctionEndPrice ?? ZERO)
				: auctionParams.auctionEndPrice;

			const thresholdPct = BigNum.from(
				priceImpactData.entryPrice,
				PRICE_PRECISION_EXP
			).scale(Env.priceDivergenceWarningThreshold, 100);

			const oracleDivergence = BigNum.from(
				auctionEndPrice?.sub(oraclePrice?.val ?? ZERO) ?? ZERO,
				PRICE_PRECISION_EXP
			).abs();

			const params = {
				auctionStartPrice,
				auctionEndPrice,
				auctionDuration: auctionParams.auctionDuration,
				constrainedBySlippage: false,
				isOracleOrder,
				isDivergentFromOracle:
					(orderType === 'market' || orderType === 'oracle') &&
					baseSizeStringValue &&
					oracleDivergence.gt(thresholdPct),
				slippage: finalSlippageToUse,
				priceImpact: priceImpactData,
			};

			return params as PreviewOrderParams;
		},
		enabled:
			// Only run query for supported order types that need server data
			(orderType === 'market' ||
				orderType === 'oracle' ||
				orderType === 'limit') &&
			// And when we have required data
			!!(oraclePrice && !oraclePrice.eqZero()) &&
			!!(markPrice && !markPrice.eqZero()),
		staleTime: 1000, // Consider data stale after 1 second (prevents spam)
		gcTime: 30 * 1000, // Keep in cache for 30 seconds after last use
		retry: 1, // Only retry once on failure
		refetchOnWindowFocus: false, // Don't refetch when window regains focus
		refetchInterval: FETCH_INTERVAL_MS,
	});

	// Create a default value for when data hasn't been fetched yet
	const defaultPreviewOrderParams: PreviewOrderParams = {
		auctionStartPrice: oraclePrice?.val || markPrice?.val || ZERO,
		auctionEndPrice: oraclePrice?.val || markPrice?.val || ZERO,
		auctionDuration: 0,
		slippage: typeof slippageTolerance === 'number' ? slippageTolerance : 0.1,
		constrainedBySlippage: false,
		isOracleOrder: false,
		isDivergentFromOracle: false,
		priceImpact: {
			entryPrice: oraclePrice?.val || markPrice?.val || ZERO,
			markPrice: markPrice?.val || ZERO,
			priceImpact: ZERO,
			baseAvailable: ZERO,
			bestPrice: oraclePrice?.val || markPrice?.val || ZERO,
			worstPrice: oraclePrice?.val || markPrice?.val || ZERO,
			priceImpactInputBaseSize: queryParams.actualBaseSize,
			showPriceEstimateOracleDivergenceWarning: false,
			exceedsLiquidity: false,
			marketIndex: targetMarketId?.marketIndex ?? 0,
			marketType: targetMarketId?.marketType ?? MarketType.PERP,
		},
	};

	return previewOrderParams || defaultPreviewOrderParams;
};

export default usePreviewOrder;
